import IframeViewer from 'core/modules/atoms/IframeViewer/IframeViewer';
import { formatSizeUnits } from 'core/utils/utils';
import { CONTENT_TYPES, typeContentsIcons } from 'core/variables/constant';
import PropTypes from 'prop-types';
import React from 'react';
import { Card, DialogUploadFile, Icon, ProgressBar, TextHtml } from 'ui';
import Tooltip from 'ui/atoms/Tooltip/Tooltip';
import useAttachmentCard from './useAttachmentCard';

const AttachmentCard = ({
  content,
  guid,
  type = 'CTTY_05',
  title = 'File name',
  size = 10000,
  onDelete = null,
  isUploading = false,
  uploadProgress = 0,
  onShowVisor = null,
  isDisabled = false,
  isEmbed = false,
  headerInfo = '',
  onAddContent,
  onUploadFiles,
  uploadedFiles,
  onDeleteUploadedFile,
  onPreviewUploadedFile,
  isReadOnly = false,
  shouldHideFileSize = false,
  style = 'default',
}) => {
  const {
    addContentReasonId,
    handleAddContent,
    isAddContentDisabled,
    isUploadDialogOpen,
    handleOpenUploadDialog,
    handleCloseUploadDialog,
    handleUploadSuccess,
    handleShowVisor,
    handleDelete,
    makeHandlePreviewFile,
    makeHandleDeleteUploadedFile,
    itemTitleClass,
    hasScrollableList,
    t,
  } = useAttachmentCard({
    content,
    guid,
    onAddContent,
    onDelete,
    onUploadFiles,
    onShowVisor,
    onPreviewUploadedFile,
    onDeleteUploadedFile,
    style,
    uploadedFiles,
  });

  function renderUploadedFileContent(file, index) {
    if (isReadOnly) {
      return (
        <div className="attachment-card__item-info">
          <span className="attachment-card__item-title attachment-card__item-title--file">{file.name}</span>
        </div>
      );
    }

    const isDownloadableUrl = file.url && !file.url.startsWith('blob:');
    if (!isDownloadableUrl) {
      return (
        <div className="attachment-card__item-info">
          <span className="attachment-card__item-title attachment-card__item-title--file">{file.name}</span>
          {!shouldHideFileSize && <span className="attachment-card__size attachment-card__size--file">{formatSizeUnits(file.size)}</span>}
        </div>
      );
    }

    if (onPreviewUploadedFile && !file.isExisting) {
      return (
        <button type="button" className="attachment-card__item-info" onClick={makeHandlePreviewFile(file, index)}>
          <span className="attachment-card__item-title attachment-card__item-title--file">{file.name}</span>
          {!shouldHideFileSize && <span className="attachment-card__size attachment-card__size--file">{formatSizeUnits(file.size)}</span>}
        </button>
      );
    }

    return (
      <a
        aria-label={t('common:Open file in new tab', { name: file.name })}
        className="attachment-card__item-info"
        href={file.url}
        rel="noopener noreferrer"
        target="_blank"
      >
        <span className="attachment-card__item-title attachment-card__item-title--link">{file.name}</span>
        {!shouldHideFileSize && <span className="attachment-card__size attachment-card__size--file">{formatSizeUnits(file.size)}</span>}
        <Icon name="external-link-2" size="md" color="grey-02" />
      </a>
    );
  }

  return (
    <Card className="attachment-card">
      {headerInfo && <div className="attachment-card__header">{headerInfo}</div>}
      {onAddContent && (
        <div className="attachment-card__actions">
          <button type="button" className="attachment-card__action-btn" onClick={handleOpenUploadDialog}>
            <Icon name="upload-line" color="primary" size="sm" />
            <span>{t('common:Upload file')}</span>
          </button>
          <Tooltip title={isAddContentDisabled ? t('common:Solo se permite un enlace por tarea o quiz') : ''} placement="top" variant="dark" hasArrow>
            <button
              type="button"
              aria-describedby={isAddContentDisabled ? addContentReasonId : undefined}
              aria-disabled={isAddContentDisabled || undefined}
              className="attachment-card__action-btn"
              onClick={handleAddContent}
            >
              <Icon name="link-content" color={isAddContentDisabled ? 'grey-03' : 'primary'} size="sm" />
              <span>{t('common:Enlazar contenido')}</span>
            </button>
          </Tooltip>
          {isAddContentDisabled && (
            <span className="visually-hidden" id={addContentReasonId}>
              {t('common:Solo se permite un enlace por tarea o quiz')}
            </span>
          )}
        </div>
      )}
      <div className={`attachment-card__items-wrapper${hasScrollableList ? ' attachment-card__items-wrapper--scrollable' : ''}`}>
        {(guid || (!onAddContent && content)) && (
          <>
            <div className="attachment-card__item">
              <div className="attachment-card__item-content">
                {style === 'link' ? (
                  <Icon name={typeContentsIcons[type]} size="md" color="grey-02" />
                ) : (
                  <div className="attachment-card__item-type-icon">
                    <Icon name={typeContentsIcons[type]} size="sm" color="white" />
                  </div>
                )}
                {onShowVisor ? (
                  <button type="button" className="attachment-card__item-info" onClick={handleShowVisor}>
                    <span className={itemTitleClass}>
                      <TextHtml text={title} />
                    </span>
                    {!isUploading && type !== CONTENT_TYPES.LINK && (
                      <span className="attachment-card__size attachment-card__size--file">{formatSizeUnits(size)}</span>
                    )}
                    {style === 'link' && <Icon name="external-link-2" size="md" color="grey-02" />}
                    {isUploading && (
                      <div aria-live="polite" className="attachament-card__progress">
                        <ProgressBar isLabelHidden label={t('common:Uploading file')} progress={Number.parseInt(uploadProgress || 0)} />
                      </div>
                    )}
                  </button>
                ) : (
                  <div className="attachment-card__item-info">
                    <span className={itemTitleClass}>
                      <TextHtml text={title} />
                    </span>
                    {!isUploading && type !== CONTENT_TYPES.LINK && (
                      <span className="attachment-card__size attachment-card__size--file">{formatSizeUnits(size)}</span>
                    )}
                    {style === 'link' && <Icon name="external-link-2" size="md" color="grey-02" />}
                    {isUploading && (
                      <div aria-live="polite" className="attachament-card__progress">
                        <ProgressBar isLabelHidden label={t('common:Uploading file')} progress={Number.parseInt(uploadProgress || 0)} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!isUploading && !isDisabled && onDelete && (
                <button type="button" aria-label={t('actions:Delete file', { name: title })} className="attachment-card__delete" onClick={handleDelete}>
                  <Icon name="trash" size="lg" color="grey-02" />
                </button>
              )}
            </div>
            {!isUploading && type === CONTENT_TYPES.LINK && isEmbed && (
              <IframeViewer url={content.contentUrl} size="md" aspectRatio="widescreen" title={content.contentName} />
            )}
          </>
        )}
        {uploadedFiles?.length > 0 && (
          <ul className="attachment-card__items-list">
            {uploadedFiles.map((file, index) => (
              <li key={`${file.name}-${index}`} className="attachment-card__item">
                <div className="attachment-card__item-content">
                  <div className="attachment-card__item-type-icon">
                    <Icon name={typeContentsIcons[file.type]} size="sm" color="white" />
                  </div>
                  {renderUploadedFileContent(file, index)}
                </div>
                {onDeleteUploadedFile && (
                  <button
                    type="button"
                    aria-label={t('actions:Delete file', { name: file.name })}
                    className="attachment-card__delete"
                    onClick={makeHandleDeleteUploadedFile(index)}
                  >
                    <Icon name="trash" size="lg" color="grey-02" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <DialogUploadFile isOpen={isUploadDialogOpen} onClose={handleCloseUploadDialog} onSuccess={handleUploadSuccess} />
    </Card>
  );
};

AttachmentCard.propTypes = {
  content: PropTypes.shape({
    url: PropTypes.string,
    contentUrl: PropTypes.string,
    contentName: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
  }),
  guid: PropTypes.string,
  onDelete: PropTypes.func,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  onShowVisor: PropTypes.func,
  isDisabled: PropTypes.bool,
  isEmbed: PropTypes.bool,
  type: PropTypes.string,
  title: PropTypes.string,
  size: PropTypes.number,
  headerInfo: PropTypes.string,
  onAddContent: PropTypes.func,
  onUploadFiles: PropTypes.func,
  uploadedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      size: PropTypes.number,
      type: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  onDeleteUploadedFile: PropTypes.func,
  onPreviewUploadedFile: PropTypes.func,
  isReadOnly: PropTypes.bool,
  shouldHideFileSize: PropTypes.bool,
  style: PropTypes.oneOf(['link', 'default']),
};

export default AttachmentCard;
